import React, {
	useCallback,
	type KeyboardEvent,
	type SyntheticEvent,
	type FocusEvent,
} from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { visuallyHiddenStyles } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { AI_BRANDNAME } from '../../common/constants.tsx';
import { AiIcon } from '../../common/ui/ai-icon/index.tsx';
import {
	usePrompt,
	usePromptActions,
	usePromptIsLoading,
	usePromptIsSubmitted,
	useScopedId,
} from '../../controllers/prompt-state/index.tsx';
import { useAnimatedPlaceholder } from '../../controllers/use-animated-placeholder/index.tsx';
import { ShortcutButtons, GO_BUTTON_SELECTOR } from './shortcut-buttons/index.tsx';
import { messages } from './messages.tsx';

type Props = {
	autoFocus?: boolean;
	backButtonLabel?: string;
	placeholder: string | string[];
	onBack?: () => void;
	onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
};

const buttonClicked = {
	action: 'clicked',
	actionSubject: 'button',
};

export const SearchField = ({
	autoFocus,
	backButtonLabel,
	placeholder,
	onBack,
	onFocus,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [searchFieldId] = useScopedId('ai-search-field');
	const { formatMessage } = useIntl();

	const [prompt] = usePrompt();
	const [isLoading] = usePromptIsLoading();
	const [isSubmitted] = usePromptIsSubmitted();
	const { onUpdate, onSubmit } = usePromptActions();
	const isEmpty = prompt.trim().length === 0;

	const handleSubmit = useCallback(
		(
			e: SyntheticEvent<HTMLElement>,
			analyticsEvent: UIAnalyticsEvent,
			...attributes: [] | [Attributes]
		) => {
			if (isEmpty) {
				return;
			}

			onSubmit(prompt);
			fireUIAnalytics(analyticsEvent, 'aiInlinePromptSubmit', ...attributes);
		},
		[isEmpty, onSubmit, prompt],
	);

	const handleBack = useCallback(
		(
			e: SyntheticEvent<HTMLElement>,
			analyticsEvent: UIAnalyticsEvent,
			...attributes: [] | [Attributes]
		) => {
			if (!onBack) return;

			onBack();
			fireUIAnalytics(analyticsEvent, 'aiInlinePromptBack', ...attributes);
		},
		[onBack],
	);

	const handleEnter = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			const analyticsEvent = createAnalyticsEvent(buttonClicked);
			handleSubmit(e, analyticsEvent, { keyboardShortcut: true });
		},
		[createAnalyticsEvent, handleSubmit],
	);

	const handleEscape = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			const analyticsEvent = createAnalyticsEvent(buttonClicked);
			handleBack(e, analyticsEvent, { keyboardShortcut: true });
		},
		[createAnalyticsEvent, handleBack],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Escape') {
				handleEscape(event);
			}

			if (event.key === 'Enter') {
				handleEnter(event);
			}
		},
		[handleEnter, handleEscape],
	);

	const handleChange = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			const nextValue = event.currentTarget.value;
			onUpdate(nextValue);
		},
		[onUpdate],
	);

	const placeholderToRender = useAnimatedPlaceholder(placeholder, prompt.length === 0);

	return (
		<TextfieldContainer isSubmitted={isSubmitted}>
			<Textfield
				id={searchFieldId}
				autoFocus={autoFocus}
				defaultValue={prompt}
				onKeyDown={handleKeyDown}
				onChange={handleChange}
				onFocus={onFocus}
				placeholder={placeholderToRender}
				appearance="none"
				isCompact={false}
				maxLength={fg('ai_search_character_limit_increase_to_1200') ? 1200 : 300}
				elemBeforeInput={<AiIcon label={AI_BRANDNAME} size="small" />}
				elemAfterInput={
					<ShortcutButtons
						backButtonLabel={backButtonLabel}
						isLoading={isLoading}
						isSubmitDisabled={isEmpty}
						onSubmit={handleSubmit}
						onBack={onBack ? handleBack : undefined}
					/>
				}
				aria-label={
					fg('gravityai-211-nl-searchfield-aria-label')
						? formatMessage(messages.enterQueryAriaLabel)
						: undefined
				}
			/>
		</TextfieldContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextfieldContainer = styled.div<{ isSubmitted: boolean }>(
	{
		marginInline: token('space.150'),
		marginBlock: token('space.100'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& [data-ds--text-field--container]': {
			minHeight: token('space.300'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			overflow: 'visible !important',

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'> svg': {
				flexShrink: 0,
				marginRight: token('space.100'),
			},

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& [data-ds--text-field--input]': {
				height: token('space.300'),
				padding: 0,
			},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		// If the prompt has been submitted then only show 'Go' button when textfield is focused
		props.isSubmitted && {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			[`&:not(:focus-within) ${GO_BUTTON_SELECTOR}`]: visuallyHiddenStyles,
		},
);
